import { getApiBaseUrl } from './config.ts';
import { readLiFatId, liFatIdFromUrl } from './li-fat-id.ts';

/**
 * Ad-conversion beacon sinks for the declarative `data-gp-conversion-*`
 * instrumentation. The observers in instrumentation.ts detect the trigger
 * (click, impression) and call in here to fire; this module owns only the
 * li_fat_id gating and the POST to `/api/conversions/v1`, which fans the event
 * out to the ad platforms server-side. Best-effort and must never block or break
 * the interaction it rides on. See docs/martech.md.
 */

/**
 * A click conversion (e.g. the Sign Up CTA). Gated on an in-window `li_fat_id`
 * cookie: the click can come after some in-app navigation, so it counts whenever
 * this browser carries an attributable ad click — not only on the landing.
 */
export function reportInteractConversion(name: string): void {
  const bridge = readLiFatId();
  if (!bridge) return; // no in-window ad click → nothing to attribute
  send(name, bridge.liFatId, bridge.capturedAt);
}

/**
 * A page-view conversion (e.g. the homepage ad landing). Gated on `li_fat_id`
 * being on this load's URL — present only on the ad landing, not on an organic
 * visit (which may still hold an in-window cookie) or an in-app return to the
 * page (a clean URL). The home element impresses once per load, so this fires
 * once per ad landing.
 */
export function reportImpressionConversion(name: string): void {
  const liFatId = liFatIdFromUrl();
  if (!liFatId) return; // not an ad landing → the view isn't ad-attributable
  send(name, liFatId, Date.now());
}

function send(name: string, liFatId: string, liFatCapturedAt: number): void {
  try {
    // `keepalive` lets the POST outlive the cross-origin navigation a click
    // usually triggers. Best-effort: never surface an error or block the page.
    void fetch(`${getApiBaseUrl()}/api/conversions/v1`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, liFatId, liFatCapturedAt }),
      keepalive: true
    }).catch(() => {});
  } catch {
    // A telemetry beacon must never break a navigation.
  }
}
